import * as yup from "yup";
import { ReactSelectOptionSchema } from "./";
import { Contact } from "../../models/contact";
import { Country, CountryInfoToCountry } from "../../models/country";
import { ReactSelectOption, Address } from "../../models/generic";

export interface AddressMinData {
    city?: string;
    state?: string;
    postalCode?: string;
    countryCode: ReactSelectOption | Country | null;
}

export interface AddressData extends AddressMinData {
    contactTypeId: number;
    address1?: string;
    address2?: string;
    address3?: string;
}

export const DefaultUSCountry: ReactSelectOption & Country = {
    label: "United States",
    value: "US",
    isoCode: "US",
    isoCode3: "USA",
    name: "United States",
    phoneCode: "1",
    sort: 1,
    states: [],
};

export const DefaultAddress: AddressData = {
    contactTypeId: 0,
    countryCode: { ...DefaultUSCountry },
};

const stateValidationCondition = (countryCode?: ReactSelectOption) => {
    const code = (countryCode ? countryCode.value : "").toString();
    if (["AU", "CA", "MX", "US"].indexOf(code) > -1)
        return yup.string().required().max(2).label("State of $parent");
    return yup.string().label("State of $parent");
};

export const AddressMinSchema = yup.object<AddressMinData>().shape({
    countryCode: ReactSelectOptionSchema.label("Country of $parent"),
    city: yup.string().label("City of $parent").max(50),
    state: yup.string().max(50),
    postalCode: yup.string().label("Postal code of $parent").max(20),
});

export const AddressMinSchemaRequired = yup.object<AddressMinData>().shape({
    countryCode: ReactSelectOptionSchema.required().notOneOf([null, undefined], "Country of $parent is required"),
    city: yup.string().required(),
    state: yup.string().required().when(["countryCode"], stateValidationCondition),
    postalCode: yup.string().required(),
}).concat(AddressMinSchema);

export const AddressMinNoZipSchemaRequired = yup.object<AddressMinData>().shape({
    countryCode: ReactSelectOptionSchema.required().notOneOf([null, undefined], "Country of $parent is required"),
    city: yup.string(),
    state: yup.string().when(["countryCode"], stateValidationCondition),
}).concat(AddressMinSchema);

export const AddressSchema = yup.object<AddressData>().shape({
    contactTypeId: yup.number().label("Type of $parent"),
    countryCode: ReactSelectOptionSchema.label("Country of $parent"),
    address1: yup.string().label("Line 1 of $parent").max(100),
    address2: yup.string().label("Line 2 of $parent").max(100),
    address3: yup.string().label("Line 3 of $parent").max(100),
    city: yup.string().label("City of $parent").max(50),
    state: yup.string().max(50),
    postalCode: yup.string().label("Postal code of $parent").max(20),
});

export const AddressSchemaRequired = yup.object<AddressData>().shape({
    contactTypeId: yup.number().required(),
    countryCode: ReactSelectOptionSchema.required().notOneOf([null, undefined], "Country of $parent is required"),
    address1: yup.string().required(),
    city: yup.string().required(),
    state: yup.string().when(["countryCode"], stateValidationCondition),
    postalCode: yup.string().required(),
}).concat(AddressSchema);

export const AddressFormToPostData = (a: AddressData) => {
    return {
        address1: a.address1,
        address2: a.address2,
        address3: a.address3,
        city: a.city,
        state: a.state,
        postalCode: a.postalCode,
        countryCode: (a.countryCode as any).value,
    };
};

export const AddressModelToForm = (a: Address): AddressData => {
    return {
        address1: a.address1 || "",
        address2: a.address2 || "",
        address3: a.address3 || "",
        city: a.city || "",
        contactTypeId: 0,
        state: a.state || "",
        postalCode: a.postalCode || "",
        countryCode: {
            ...CountryInfoToCountry(a),
            label: a.country,
            value: a.countryCode,
        },
    };
};

export const ContactModelToForm = (a: Contact): AddressData => {
    return {
        address1: a.address1 || "",
        address2: a.address2 || "",
        address3: a.address3 || "",
        city: a.city || "",
        contactTypeId: a.contactTypeId,
        state: a.state || "",
        postalCode: a.postalCode || "",
        countryCode: {
            ...CountryInfoToCountry(a),
            label: a.country,
            value: a.countryCode,
        },
    };
};



// WEBPACK FOOTER //
// ./src/forms/generic/Address.ts